import {isString} from '@naverpay/hidash/isString'
import {format, isToday, isTomorrow, isValid, isYesterday, parse} from 'date-fns'
import {ko} from 'date-fns/locale'

import {DateFormat, DateFormatType, TimeFormatType} from './time'

/**
 * Date 값을 포맷팅합니다. 포맷팅이 불가능한 date 가 입력된 경우 null 을 반환합니다.
 * @param {Date | number | string} date 변환할 Date
 * @param {DateFormatType | TimeFormatType} formatString 변환할 표시 포맷
 * @param {string} option.inputFormat input format (ex. 'yyyyMMdd')
 * @returns Maybe<string>
 */
export const formatDateFns = (
    date: Date | number | string | TIMESTAMP,
    formatString: DateFormatType | TimeFormatType,
    option?: {inputFormat: string},
) => {
    if (isString(date) && option?.inputFormat) {
        try {
            return format(parse(date, option.inputFormat, new Date()), formatString, {
                locale: ko,
            })
        } catch {
            return null
        }
    }

    if (typeof date === 'string' && !Date.parse(date)) {
        return null
    }
    return format(new Date(date), formatString, {locale: ko})
}

// 오늘/내일/어제 여부를 확인하고, 해당 날짜를 한국어로 표현하는 함수. 해당하지 않으면 formatString으로 포매팅한 값 반환
export const formatDateToRelativeKoreanDate = (
    date: Date | number | string | TIMESTAMP,
    formatString: DateFormatType | TimeFormatType,
) => {
    if (isTomorrow(date)) {
        return `내일`
    } else if (isToday(date)) {
        return `오늘`
    } else if (isYesterday(date)) {
        return `어제`
    } else {
        return formatDateFns(date, formatString)
    }
}

/** 자주 사용되는 date format 공통화 */
export const getDateText = (date: Maybe<Date | number | string | TIMESTAMP>) => {
    try {
        return date ? (formatDateFns(date, DateFormat['yyyy-MM-dd']) ?? '') : ''
    } catch {
        return ''
    }
}

/** 자주 사용되는 date format 공통화 > month */
export const getMonthDateText = (date: Maybe<Date | number | string | TIMESTAMP>) => {
    try {
        return date ? (formatDateFns(date, DateFormat['yyyy-MM']) ?? '') : ''
    } catch {
        return ''
    }
}

/** date: '202506618', time: '1950' 형태의 파라미터로 받은 문자열을 ISOString 포맷으로 변환. 변환 실패 시 undefined 반환. */
export const makeISODateString = ({date, time}: {date?: string; time?: string}) => {
    if (!date || !time || date.length !== 8 || time.length !== 4) {
        return undefined
    }

    const yyyy = date?.slice(0, 4)
    const MM = date?.slice(4, 6)
    const dd = date?.slice(6, 8)
    const hh = time?.slice(0, 2)
    const mm = time?.slice(2, 4)

    const result = new Date(`${yyyy}-${MM}-${dd}T${hh}:${mm}:00`)

    if (!isValid(result)) {
        return undefined
    }

    return result.toISOString()
}

/**
 * date: YYYYMMdd, time: HHmm 형태의 파라미터로 받은 문자열 기준 Date 정보 반환 변환 실패 시 undefined
 */
export const makeDateFromRawFormat = ({date, time}: {date?: string; time?: string}) => {
    const yyyy = date?.slice(0, 4)
    const mm = date?.slice(4, 6)
    const dd = date?.slice(6, 8)
    const hh = time?.slice(0, 2)
    const min = time?.slice(2, 4)

    const result = new Date(`${yyyy}-${mm}-${dd}T${hh}:${min}:00`)

    if (isValid(result)) {
        return result
    } else {
        return undefined
    }
}
